import { InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for feedstock characteristics
 */
export interface FeedstockCharacteristicsInterface {
  characteristicsEnabled: boolean;
  vs: UnitValue;
  ts: UnitValue;
  density: UnitValue;
  densityEnabled: boolean;
  cod: UnitValue;
  codEnabled: boolean;
}

/**
 * constructs the FeedstockCharacteristics class.
 * @param param is used to construct the class. If null or not provided, the class will be set to default values
 */
export class FeedstockCharacteristics {
  characteristicsEnabled: boolean;
  vs: UnitValue;
  ts: UnitValue;
  density: UnitValue;
  densityEnabled: boolean;
  cod: UnitValue;
  codEnabled: boolean;

  /**
   * constructs the FeedstockCharacteristics class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockCharacteristicsInterface = {
    characteristicsEnabled: false,
    vs: new UnitValue({value: null, unit: '000000000000000000006301'}),
    ts: new UnitValue({value: null, unit: '000000000000000000000101'}),
    density: new UnitValue(),
    densityEnabled: true,
    cod: new UnitValue(),
    codEnabled: false
  }) {
    this.characteristicsEnabled = param.characteristicsEnabled;
    this.vs = new UnitValue(param.vs);
    this.ts = new UnitValue(param.ts);
    this.density = new UnitValue(param.density);
    this.densityEnabled = param.densityEnabled ?? false;
    this.cod = new UnitValue(param.cod);
    this.codEnabled = param.codEnabled ?? false;
  }

  /**
   * returns a separate instance of the provided FeedstockCharacteristics obj
   * @param feedstockCharacteristics will be returned as a separate instance
   */
  static deepCopy(feedstockCharacteristics: FeedstockCharacteristics) {
    let copy;
    if (feedstockCharacteristics) {
      copy = new FeedstockCharacteristics(feedstockCharacteristics);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockCharacteristics');
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each available inputs. Density will only be
   * included if densityEnabled is true
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.ts, this.ts, [...path, 'ts']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.vs, this.vs, [...path, 'vs']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.densityEnabled, this.densityEnabled, [...path, 'densityEnabled']);
    if (this.densityEnabled) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.density, this.density, [...path, 'density']);
    }
    inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.codEnabled, this.codEnabled, [...path, 'codEnabled']);
    if (this.codEnabled) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockCharacteristicsPropName.cod, this.cod, [...path, 'cod']);
    }
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockCharacteristics class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockCharacteristicsPropName {
  vs = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.VS',
  ts = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.TS',
  density = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.DENSITY',
  densityEnabled = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.DENSITY_ENABLED',
  cod = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.COD',
  codEnabled = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.COD_ENABLED',
  id_vs = 'volatile solids',
  id_ts = 'total solids',
  id_density = 'density',
  id_densityEnabled = 'is density enabled',
  id_cod = 'chemical oxygen demand (COD)',
  id_codEnabled = 'is COD enabled'
}
